import React, { useState, useEffect } from 'react';
import { 
  HandCoins, Users, Calendar, Filter, 
  ArrowUpRight, Download, Search, ChevronRight,
  Wallet, PieChart, Landmark
} from 'lucide-react';

const DonationOverview = () => {
  const [dateFilter, setDateFilter] = useState('This Month');
  const [donations, setDonations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch Live Data
  useEffect(() => {
    const fetchDonations = async () => {
      try {
        // const response = await fetch("http://localhost:1415/api/donations/all");
        const token = localStorage.getItem("sevaa_admin_token");
// const response = await fetch("http://localhost/seva-backend/api/donations/all.php", {
const response = await fetch("https://www.sevaafoundation.com/seva-backend/api/donations/all.php", {
    headers: { "Authorization": `Bearer ${token}` }
});
        const data = await response.json();
        if (data.success) {
          setDonations(data.donations);
        }
      } catch (error) {
        console.error("Failed to fetch donations", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDonations();
  }, []);

  // ====== DATA CALCULATIONS ======
  const successDonations = donations.filter(d => d.payment_status === 'success');
  const pendingDonations = donations.filter(d => d.payment_status === 'pending');
  
  const totalReceived = successDonations.reduce((sum, d) => sum + Number(d.amount), 0);
  const pendingAmount = pendingDonations.reduce((sum, d) => sum + Number(d.amount), 0);
  const uniqueDonorsCount = new Set(successDonations.map(d => d.email)).size;
  const avgDonation = successDonations.length > 0 ? (totalReceived / successDonations.length) : 0;

  // Group by Campaigns for PieChart section
  const campaignGroup: Record<string, { amount: number, donors: Set<string> }> = {};
  successDonations.forEach(d => {
    const campName = d.campaign_name || "General Donation";
    if (!campaignGroup[campName]) campaignGroup[campName] = { amount: 0, donors: new Set() };
    campaignGroup[campName].amount += Number(d.amount);
    campaignGroup[campName].donors.add(d.email);
  });

  const colors = ['bg-orange-500', 'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500'];
  const campaignStats = Object.keys(campaignGroup).map((key, index) => ({
    name: key,
    amount: `₹${campaignGroup[key].amount.toLocaleString('en-IN')}`,
    rawAmount: campaignGroup[key].amount,
    donors: campaignGroup[key].donors.size,
    color: colors[index % colors.length]
  })).sort((a, b) => b.rawAmount - a.rawAmount).slice(0, 4); // Top 4 campaigns

  // Format Date Helper
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* 1. Header & Filters (Design same as before) */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Donation Analytics</h1>
          <p className="text-slate-500 text-sm mt-1">Track and manage all incoming contributions.</p>
        </div>
        <div className="flex items-center gap-3 w-full lg:w-auto">
          {/* ... existing filter inputs ... */}
          {/* <button className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-xl font-semibold hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20">
            <Download size={18} /> <span className="hidden sm:inline">Export CSV</span>
          </button> */}
        </div>
      </div>

      {/* 2. Key Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-2xl flex items-center justify-center"><Wallet size={24} /></div>
          <div>
            <p className="text-slate-500 text-xs font-bold uppercase tracking-wider">Total Received</p>
            <h3 className="text-2xl font-black text-slate-900">₹{totalReceived.toLocaleString('en-IN')}</h3>
          </div>
        </div>
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center"><Users size={24} /></div>
          <div>
            <p className="text-slate-500 text-xs font-bold uppercase tracking-wider">Unique Donors</p>
            <h3 className="text-2xl font-black text-slate-900">{uniqueDonorsCount}</h3>
          </div>
        </div>
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 bg-green-100 text-green-600 rounded-2xl flex items-center justify-center"><ArrowUpRight size={24} /></div>
          <div>
            <p className="text-slate-500 text-xs font-bold uppercase tracking-wider">Avg. Donation</p>
            <h3 className="text-2xl font-black text-slate-900">₹{Math.round(avgDonation).toLocaleString('en-IN')}</h3>
          </div>
        </div>
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-2xl flex items-center justify-center"><Landmark size={24} /></div>
          <div>
            <p className="text-slate-500 text-xs font-bold uppercase tracking-wider">Bank Pending</p>
            <h3 className="text-2xl font-black text-slate-900">₹{pendingAmount.toLocaleString('en-IN')}</h3>
          </div>
        </div>
      </div>

      {/* 3. Main Content: Campaign Split & Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center">
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <PieChart size={20} className="text-orange-500" /> Campaign Wise Performance
            </h3>
          </div>
          <div className="p-6">
            {campaignStats.length === 0 ? <p className="text-slate-500 text-sm">No successful donations yet.</p> : (
              <div className="space-y-5">
                {campaignStats.map((item, index) => (
                  <div key={index} className="group cursor-default">
                    <div className="flex justify-between items-end mb-2">
                      <div>
                        <h4 className="font-bold text-slate-700 group-hover:text-orange-600 transition-colors">{item.name}</h4>
                        <p className="text-xs text-slate-400">{item.donors} Donors contributed</p>
                      </div>
                      <span className="text-lg font-black text-slate-900">{item.amount}</span>
                    </div>
                    <div className="w-full bg-slate-100 h-3 rounded-full overflow-hidden">
                      <div className={`h-full ${item.color} rounded-full transition-all duration-1000`} style={{ width: `${(item.rawAmount/totalReceived)*100}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Insights / Goal Card */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 text-white flex flex-col justify-between relative overflow-hidden shadow-xl shadow-slate-300">
           {/* Design kept exactly same */}
           <div className="absolute top-0 right-0 p-4 opacity-20"><HandCoins size={120} /></div>
           <div className="z-10">
            <h3 className="text-xl font-bold mb-2">Platform Total</h3>
            <p className="text-slate-400 text-sm">Overall successful funds</p>
            <div className="mt-8">
              <h2 className="text-4xl font-black text-orange-400">₹{totalReceived.toLocaleString('en-IN')}</h2>
            </div>
          </div>
        </div>
      </div>

      {/* 4. Recent Transactions Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
        <div className="p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h3 className="font-bold text-slate-800">Recent Transactions</h3>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input type="text" placeholder="Search donor..." className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-orange-500/20" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50/50 text-slate-500 text-xs font-bold uppercase tracking-wider">
              <tr>
                <th className="px-6 py-4">Donor</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Campaign</th>
                <th className="px-6 py-4">Amount</th>
                <th className="px-6 py-4">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? <tr><td colSpan={5} className="text-center py-4">Loading...</td></tr> : 
               donations.slice(0, 8).map((d) => (
                <tr key={d.id} className="hover:bg-slate-50/50 transition-colors group text-sm">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold text-xs uppercase">
                        {d.name.charAt(0)}
                      </div>
                      <span className="font-semibold text-slate-700">{d.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-500 font-medium">{formatDate(d.created_at)}</td>
                  <td className="px-6 py-4 font-medium text-slate-600 italic">{d.campaign_name || "General"}</td>
                  <td className="px-6 py-4 font-black text-slate-900">₹{Number(d.amount).toLocaleString('en-IN')}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 py-1 px-2.5 rounded-full text-xs font-bold ${
                      d.payment_status === 'success' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {d.payment_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DonationOverview;